import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { getAllAdjustmentReasonCodes, getAdjustmentReasonCodeInfo, AdjustmentReasonCode } from '../../../../constants/adjustmentReasonCode';
import fileAddIcon from '../../../../static/img/file-add.png';

interface AddAdjustmentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
	currency?: string;
}

const ModalOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 99999;
	padding: 20px;
`;

const ModalContent = styled.div`
	background: white;
	border-radius: 12px;
	width: 600px;
	max-width: calc(100vw - 40px);
	max-height: calc(100vh - 40px);
	overflow-y: auto;
	overflow-x: hidden;
	position: relative;
	z-index: 100000;
`;

const ModalHeader = styled.div`
	padding: 20px 20px 0 20px;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ModalTitle = styled.h2`
	font-size: 18px;
	font-weight: 700;
	color: #111827;
	margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 35px;
    color: #6B7280;
    cursor: pointer;
    padding: 0;
    font-weight: normal;
	
	&:hover {
		color: #374151;
	}
`;

const ModalBody = styled.div`
	padding: 20px;
	overflow-x: hidden;
`;

const FormRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 16px;
	margin-bottom: 16px;
	
	&.full-width {
		grid-template-columns: 1fr;
	}
`;

const FormGroup = styled.div`
	display: flex;
	flex-direction: column;
`;

const Label = styled.label`
	font-size: 14px;
	font-weight: 500;
	color: #000000;
	margin-bottom: 6px;
	
	&.required::after {
		content: ' *';
		color: #EF4444;
	}
`;

const Select = styled.select<{ hasValue?: boolean; hasError?: boolean }>`
	padding: 10px 30px 10px 12px;
	border: 1px solid ${props => props.hasError ? '#EF4444' : '#D1D5DB'};
	border-radius: 6px;
	font-size: 14px;
	background-color: white;
	cursor: pointer;
	color: ${props => props.hasValue ? '#111827' : '#878787'};
	appearance: none;
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236B7280' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 8px center;

	option {
		color: #111827;
	}
	
	option[value=""] {
		color: #878787;
	}
	
	&:focus {
		outline: none;
		border-color: ${props => props.hasError ? '#EF4444' : '#3B82F6'};
		box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
	}
`;

const Input = styled.input<{ hasValue?: boolean; hasError?: boolean }>`
	padding: 10px 12px;
	border: 1px solid ${props => props.hasError ? '#EF4444' : '#D1D5DB'};
	border-radius: 6px;
	font-size: 14px;
	color: ${props => props.hasValue ? '#111827' : '#878787'};
	
	&::placeholder {
		color: #878787;
	}
	
	&:focus {
		outline: none;
		border-color: ${props => props.hasError ? '#EF4444' : '#3B82F6'};
		box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
		color: #111827;
	}
`;

const TextArea = styled.textarea<{ hasValue?: boolean; hasError?: boolean }>`
	padding: 10px 12px;
	border: 1px solid ${props => props.hasError ? '#EF4444' : '#D1D5DB'};
	border-radius: 6px;
	font-size: 14px;
	min-height: 80px;
	resize: vertical;
	color: ${props => props.hasValue ? '#111827' : '#878787'};
	
	&::placeholder {
		color: #878787;
        font-weght: 400;
	}
	
	&:focus {
		outline: none;
		border-color: ${props => props.hasError ? '#EF4444' : '#3B82F6'};
		box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
		color: #111827;
	}
`;

const ErrorMessage = styled.div`
	font-size: 12px;
	color: #EF4444;
	margin-top: 4px;
`;


const AddFileLink = styled.button`
	background: none;
	border: none;
	color: #6B7280;
	font-size: 14px;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
	margin-top: 8px;
	margin-left: auto;
	
	&:hover {
		color: #6B7280;
	}
	
	&:disabled {
		color: #9CA3AF;
		cursor: not-allowed;
	}
`;

const AddFileLinkWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	width: 100%;
	margin-top: 8px;
`;

const FileItemContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 12px;
	width: 100%;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
`;

const FileInputWrapper = styled.div`
	display: flex;
	align-items: center;
    border: 1px solid #C0BFBF;
	border-radius: 5px;
	background-color: white;
	padding: 5px;
	height: 40px;
	flex: 1;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
`;

const FileItemInput = styled.input`
	display: none;
`;

const FileChooseButton = styled.label`
    background-color: #EFEFEF;
    color: #6B7280;
    border-image: initial;
    border-radius: 5px;
    padding: 8px 13px;
    font-size: 14px;
    cursor: pointer;
    height: 100%;
    display: flex;
    -webkit-box-align: center;
    align-items: center;
    margin: 0px;
    border: 1px solid #878787;
    flex-shrink: 0;
	
	&:hover {
		background-color: #D1D5DB;
	}
`;

const FileInfo = styled.div`
	font-size: 14px;
	color: #111827;
	margin-left: 12px;
	flex: 1;
	min-width: 0;
	max-width: 100%;
	display: flex;
	align-items: center;
	gap: 8px;
	overflow: hidden;
`;

const FileName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
	max-width: 330px;
	flex: 1;
`;

const FileSize = styled.span`
	color: #9CA3AF;
	flex-shrink: 0;
`;

const FileError = styled.div`
	font-size: 12px;
	color: #EF4444;
	margin-top: 4px;
`;

const RemoveFileButton = styled.button`
	background: #EF4444;
	color: white;
	border: none;
	padding: 8px 12px;
	border-radius: 6px;
	font-size: 14px;
	cursor: pointer;
	
	&:hover {
		background: #DC2626;
	}
`;

const ModalFooter = styled.div`
	padding: 0 20px 20px 20px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
`;

const ActionButton = styled.button<{ variant: 'cancel' | 'submit' }>`
	padding: 10px 15px;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	
	${props => {
        switch (props.variant) {
            case 'cancel':
                return `
					background: white;
					color: #111827;
					border: 1px solid #D1D5DB;
					
					&:hover {
						background: white;
					}
				`;
            case 'submit':
                return `
					background: #2563EB;
					color: white;
					border: 1px solid #2563EB;
					
					&:hover {
						background: #2563EB;
					}
				`;
        }
    }}
`;

interface FileInputItem {
    id: string;
    file: File | null;
    error?: string;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default function AddAdjustmentModal({
    isOpen,
    onClose,
    onSubmit,
	currency = 'USD'
}: AddAdjustmentModalProps) {
    const [formData, setFormData] = useState({
        adjustmentType: '',
        amount: '0.00',
        reasonCode: '',
        description: ''
    });

    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const mapTypeToNumber = (type: string): string => {
        if (type === 'increase') return '10';
        if (type === 'decrease') return '20';
        return '';
    };
    const [fileItems, setFileItems] = useState<FileInputItem[]>([
        { id: '1', file: null, error: undefined }
    ]);

    useEffect(() => {
        if (isOpen) {
            setFormData({
                adjustmentType: '',
                amount: '0.00',
                reasonCode: '',
                description: ''
            });
            setFileItems([{ id: '1', file: null, error: undefined }]);
            setErrors({});
        }
    }, [isOpen]);

    const handleInputChange = (field: string, value: string) => {
        if (field === 'reasonCode' && value) {
            const reasonInfo = getAdjustmentReasonCodeInfo(value);
            if (reasonInfo) {
                if (reasonInfo.defaultType === 'both') {
                    // Nếu là 'both' (OTHER), cho phép chọn type thủ công - reset type
                    setFormData(prev => ({
                        ...prev,
                        [field]: value,
                        adjustmentType: '' // Reset type khi chọn reason code 'both'
                    }));
                } else {
                    setFormData(prev => ({
                        ...prev,
                        [field]: value,
                        adjustmentType: mapTypeToNumber(reasonInfo.defaultType)
                    }));
                }
                // Clear errors for both fields
                if (errors.reasonCode) {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.reasonCode;
                        return newErrors;
                    });
                }
                if (errors.adjustmentType) {
                    setErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.adjustmentType;
                        return newErrors;
                    });
                }
                return;
            }
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        // Clear error khi user bắt đầu nhập
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleFileChange = (id: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        let error: string | undefined = undefined;

        if (file) {
            if (file.size > MAX_FILE_SIZE) {
                error = `File quá lớn. Kích thước tối đa là 5MB (File hiện tại: ${(file.size / 1024 / 1024).toFixed(2)}MB)`;
                e.target.value = '';
            }
        }

        setFileItems(prev =>
            prev.map(item =>
                item.id === id ? { ...item, file: error ? null : file, error } : item
            )
        );
    };

    const handleAddFileItem = () => {
        if (fileItems.length < 10) {
            const newId = String(Date.now());
            setFileItems(prev => [...prev, { id: newId, file: null, error: undefined }]);
        }
    };

    const handleRemoveFileItem = (id: string) => {
        if (fileItems.length > 1) {
            setFileItems(prev => prev.filter(item => item.id !== id));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.adjustmentType || formData.adjustmentType.trim() === '') {
            newErrors.adjustmentType = 'Vui lòng chọn loại điều chỉnh';
        }

        if (!formData.amount || formData.amount === '0' || formData.amount === '0.00' || parseFloat(formData.amount) <= 0) {
            newErrors.amount = 'Số tiền phải lớn hơn 0';
        }

        if (!formData.reasonCode || formData.reasonCode.trim() === '') {
            newErrors.reasonCode = 'Vui lòng chọn mã lý do';
        }

        if (!formData.description || formData.description.trim() === '') {
            newErrors.description = 'Vui lòng nhập mô tả chi tiết';
        }

        const filesWithErrors = fileItems.filter(item => item.file && item.file.size > MAX_FILE_SIZE);
        if (filesWithErrors.length > 0) {
            newErrors.files = 'Một hoặc nhiều file vượt quá giới hạn 5MB. Vui lòng kiểm tra lại.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        const files = fileItems.map(item => item.file).filter(Boolean) as File[];
        onSubmit({
            ...formData,
            files: files.length > 0 ? files : []
        });
    };

    const handleClose = () => {
        setFormData({
            adjustmentType: '',
            amount: '0.00',
            reasonCode: '',
            description: ''
        });
        setFileItems([{ id: '1', file: null, error: undefined }]);
        onClose();
    };

    if (!isOpen) return null;

    // Kiểm tra xem có phải reason code "Khác" (OTHER) không
    const isOtherReason = formData.reasonCode === AdjustmentReasonCode.OTHER;
    const isAdjustmentTypeDisabled = !isOtherReason;

    const modalContent = (
        <ModalOverlay>
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>Thêm điều chỉnh cấp công nợ</ModalTitle>
                    <CloseButton onClick={handleClose}>×</CloseButton>
                </ModalHeader>

                <ModalBody>
                    <form onSubmit={handleSubmit}>
                        <FormRow>
                            <FormGroup>
                                <Label className="required">Loại điều chỉnh</Label>
                                <Select
                                    value={formData.adjustmentType}
                                    onChange={(e) => handleInputChange('adjustmentType', e.target.value)}
                                    required
                                    hasValue={!!formData.adjustmentType}
                                    hasError={!!errors.adjustmentType}
                                    disabled={isAdjustmentTypeDisabled}
                                    style={isAdjustmentTypeDisabled ? { 
                                        cursor: 'not-allowed', 
                                        backgroundColor: '#F3F4F6',
                                        color: formData.adjustmentType ? '#111827' : '#878787'
                                    } : {}}
                                >
                                    <option value="">-- Chọn loại --</option>
                                    <option value="10">Tăng chi phí</option>
                                    <option value="20">Giảm chi phí</option>
                                </Select>
                                {errors.adjustmentType && <ErrorMessage>{errors.adjustmentType}</ErrorMessage>}
                            </FormGroup>
                            <FormGroup>
                                                        <Label className="required">Số tiền ({currency})</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.amount}
                                    onChange={(e) => handleInputChange('amount', e.target.value)}
                                    required
                                    hasValue={!!formData.amount && formData.amount !== '0.00'}
                                    hasError={!!errors.amount}
                                    placeholder="0.00"
                                />
                                {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
                            </FormGroup>
                        </FormRow>

                        <FormRow className="full-width">
                            <FormGroup>
                                <Label className="required">Mã lý do</Label>
                                <Select
                                    value={formData.reasonCode}
                                    onChange={(e) => handleInputChange('reasonCode', e.target.value)}
                                    required
                                    hasValue={!!formData.reasonCode}
                                    hasError={!!errors.reasonCode}
                                >
                                    <option value="">-- Chọn mã lý do --</option>
                                    {getAllAdjustmentReasonCodes().map((reason) => (
                                        <option key={reason.value} value={reason.value}>
                                            {reason.value} - {reason.description}
                                        </option>
                                    ))}
                                </Select>
                                {errors.reasonCode && <ErrorMessage>{errors.reasonCode}</ErrorMessage>}
                            </FormGroup>
                        </FormRow>

                        <FormRow className="full-width">
                            <FormGroup>
                                <Label className="required">Mô tả chi tiết</Label>
                                <TextArea
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder="Nhập mô tả chi tiết về lý do điều chỉnh công nợ..."
                                    required
                                    hasValue={!!formData.description}
                                    hasError={!!errors.description}
                                />
                                {errors.description && <ErrorMessage>{errors.description}</ErrorMessage>}
                            </FormGroup>
                        </FormRow>

                        <FormRow className="full-width">
                            <FormGroup>
                                <Label>Tài liệu đính kèm (nếu có)</Label>
                                <div>
                                    {fileItems.map((item) => (
                                        <div key={item.id}>
                                            <FileItemContainer>
                                                <FileInputWrapper>
                                                    <FileItemInput
                                                        type="file"
                                                        id={`file-upload-${item.id}`}
                                                        onChange={(e) => handleFileChange(item.id, e)}
                                                    />
                                                    <FileChooseButton htmlFor={`file-upload-${item.id}`}>
                                                        Choose Files
                                                    </FileChooseButton>
                                                    <FileInfo>
                                                        {item.file ? (
                                                            <>
                                                                <FileName title={item.file.name}>
                                                                    {item.file.name}
                                                                </FileName>
                                                                {item.file.size && (
                                                                    <FileSize>
                                                                        ({(item.file.size / 1024 / 1024).toFixed(2)}MB)
                                                                    </FileSize>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span>No file choose</span>
                                                        )}
                                                    </FileInfo>
                                                </FileInputWrapper>
                                                {fileItems.length > 1 && (
                                                    <RemoveFileButton
                                                        type="button"
                                                        onClick={() => handleRemoveFileItem(item.id)}
                                                    >
                                                        Xóa
                                                    </RemoveFileButton>
                                                )}
                                            </FileItemContainer>
                                            {item.error && (
                                                <FileError>{item.error}</FileError>
                                            )}
                                        </div>
                                    ))}
                                    {fileItems.length < 10 && (
                                        <AddFileLinkWrapper>
                                            <AddFileLink type="button" onClick={handleAddFileItem}>
                                                <img src={fileAddIcon} alt="Add file" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                                                Thêm file đính kèm
                                            </AddFileLink>
                                        </AddFileLinkWrapper>
                                    )}
                                    {errors.files && <ErrorMessage>{errors.files}</ErrorMessage>}
                                </div>
                            </FormGroup>
                        </FormRow>
                    </form>
                </ModalBody>

                <ModalFooter>
                    <ActionButton type="button" variant="cancel" onClick={handleClose}>
                        Hủy
                    </ActionButton>
                    <ActionButton type="submit" variant="submit" onClick={handleSubmit}>
                        Thêm điều chỉnh
                    </ActionButton>
                </ModalFooter>
            </ModalContent>
        </ModalOverlay>
    );

    return createPortal(modalContent, document.body);
}
